import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  OFFLINE_PUBLICATIONS_KEY,
  offlineKey,
  offlineKeyForEvent,
  readOfflinePublications,
  sortOfflinePublications,
  writeOfflinePublications,
  type OfflineAuthor,
  type OfflinePublication,
  type OfflinePublicationLibrary,
} from '@/lib/offlineLibrary';

/**
 * Module-level cache + subscriber list, for the same reason as
 * `useOfflineLibrary`: saving a publication must immediately update the
 * save button, the profile page's offline section, and any publication
 * cards on screen. Per-component state would let those views disagree.
 */
let cache: OfflinePublicationLibrary | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): OfflinePublicationLibrary {
  // Must be referentially stable between changes, or
  // useSyncExternalStore re-renders forever.
  cache ??= readOfflinePublications();
  return cache;
}

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== OFFLINE_PUBLICATIONS_KEY) return;
    cache = null;
    emit();
  });
}

function mutate(update: (library: OfflinePublicationLibrary) => OfflinePublicationLibrary): void {
  const next = update(getSnapshot());
  // Write first: if storage is full this throws, and the in-memory
  // cache must not claim a save that didn't actually persist.
  writeOfflinePublications(next);
  cache = next;
  emit();
}

function useStore(): OfflinePublicationLibrary {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export interface UseOfflinePublicationsResult {
  /** Saved publications, newest save first. */
  publications: OfflinePublication[];
  /** Total number of saved publications. */
  count: number;
  /** Whether this index event's publication (by `pubkey:d`) is saved. */
  isSaved: (index: NostrEvent) => boolean;
  /**
   * Saves (or refreshes) a publication and all of its sections. Throws
   * `OfflineQuotaError` if storage is full — publications are much
   * larger than single articles, so this is a realistic outcome.
   */
  save: (index: NostrEvent, sections: NostrEvent[], author?: OfflineAuthor) => void;
  /** Removes a publication (and its sections) from the offline library. */
  remove: (index: NostrEvent) => void;
  /** Removes every saved publication. */
  clear: () => void;
}

/**
 * Read/write access to the offline publication library — NKBIP-01
 * publications (kind 30040 index + its section events) the user has
 * saved into `localStorage` for reading with no relay connection.
 *
 * A publication is saved as a complete unit: the index alone is just a
 * table of contents whose entries would all fail to load offline, so
 * every resolved section is stored with it, in reading order.
 */
export function useOfflinePublications(): UseOfflinePublicationsResult {
  const library = useStore();

  const publications = useMemo(() => sortOfflinePublications(library), [library]);

  const isSaved = useCallback(
    (index: NostrEvent) => {
      const key = offlineKeyForEvent(index);
      return Boolean(key && library[key]);
    },
    [library],
  );

  const save = useCallback((index: NostrEvent, sections: NostrEvent[], author?: OfflineAuthor) => {
    const key = offlineKeyForEvent(index);
    if (!key) return;
    mutate((current) => ({
      ...current,
      [key]: { index, sections, savedAt: Date.now(), author },
    }));
  }, []);

  const remove = useCallback((index: NostrEvent) => {
    const key = offlineKeyForEvent(index);
    if (!key) return;
    mutate((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    mutate(() => ({}));
  }, []);

  return { publications, count: publications.length, isSaved, save, remove, clear };
}

/**
 * The locally-saved copy of a publication, if there is one — used by
 * `PublicationPage` as a fallback so a saved publication still renders
 * (index *and* sections) when the relays can't be reached.
 */
export function useOfflinePublication(
  pubkey: string | undefined,
  d: string | undefined,
): OfflinePublication | undefined {
  const library = useStore();

  return useMemo(() => {
    if (!pubkey || !d) return undefined;
    return library[offlineKey(pubkey, d)];
  }, [library, pubkey, d]);
}
